import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Shared drag-and-drop plumbing for the app's upload surfaces.
 *
 * Every upload in the app already had a hidden `<input type="file">` and a
 * button that clicks it. Drag-and-drop had been added ad hoc on top of a few of
 * those (the finance dropzone, the wiki pages, the agreement dialogs) and each
 * one re-derived the same four awkward details, usually getting at least one
 * wrong:
 *
 *  - `dragenter`/`dragleave` fire for every child element the cursor crosses, so
 *    a naive `onDragLeave={() => setOver(false)}` flickers the highlight off as
 *    soon as the pointer moves over the icon inside the drop zone. This tracks a
 *    counter instead, which is the standard fix.
 *  - `dragover` must call `preventDefault()` on every event or the browser
 *    refuses the drop and navigates to the file instead.
 *  - a drag carrying text or a dragged UI element is not a file drop, so the
 *    zone should not light up for it. `dataTransfer.types` says which it is
 *    before the drop happens.
 *  - validation has to run on dropped files as well as browsed ones, because a
 *    drop bypasses the input's `accept` filter entirely.
 *
 * The hook owns only those mechanics and hands back props to spread. It has no
 * opinion about markup, so a caller can put the highlight on a dialog, a panel,
 * or a whole page without fighting a wrapper component.
 */

export interface UseFileDropOptions {
  /**
   * Same syntax as an input's `accept`: ".mp3,.wav" and/or "audio/*".
   * Dropped files are checked against it since a drop skips the input filter.
   * Omit to accept anything.
   */
  accept?: string;
  /** Per-file size ceiling. Omit for no limit. */
  maxBytes?: number;
  /** Files past this count are rejected. Ignored when `multiple` is false. */
  maxFiles?: number;
  /** When false (the default) only the first dropped file is taken. */
  multiple?: boolean;
  /** Ignores drag events entirely — use while an upload is in flight. */
  disabled?: boolean;
  /** Called with the files that passed validation. Never called with an empty array. */
  onFiles: (files: File[]) => void;
  /**
   * Called with a human-readable reason when files were rejected, so the caller
   * can surface it however it already reports errors (alert bar, toast, inline).
   */
  onRejected?: (message: string) => void;
}

const matchesAccept = (file: File, accept: string): boolean => {
  const rules = accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);
  if (!rules.length) return true;

  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();

  return rules.some((rule) => {
    if (rule.startsWith('.')) return name.endsWith(rule);
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export function useFileDrop({
  accept,
  maxBytes,
  maxFiles,
  multiple = false,
  disabled = false,
  onFiles,
  onRejected,
}: UseFileDropOptions) {
  const [isDragOver, setIsDragOver] = useState(false);
  // Depth rather than a boolean: see the dragenter/dragleave note above.
  const dragDepth = useRef(0);

  const resetDrag = useCallback(() => {
    dragDepth.current = 0;
    setIsDragOver(false);
  }, []);

  const validate = useCallback(
    (incoming: File[]): File[] => {
      const accepted: File[] = [];
      const rejected: string[] = [];

      for (const file of incoming) {
        if (accept && !matchesAccept(file, accept)) {
          rejected.push(`${file.name} (unsupported type)`);
          continue;
        }
        if (maxBytes != null && file.size > maxBytes) {
          rejected.push(`${file.name} (over ${formatBytes(maxBytes)})`);
          continue;
        }
        accepted.push(file);
      }

      let allowed = accepted;
      if (!multiple) {
        allowed = accepted.slice(0, 1);
      } else if (maxFiles != null && accepted.length > maxFiles) {
        allowed = accepted.slice(0, maxFiles);
        rejected.push(`${accepted.length - maxFiles} file(s) over the ${maxFiles}-file limit`);
      }

      if (rejected.length) onRejected?.(`Skipped: ${rejected.join(', ')}`);
      return allowed;
    },
    [accept, maxBytes, maxFiles, multiple, onRejected],
  );

  /** Shared by the drop handler and any `onChange` the caller wires to its input. */
  const acceptFiles = useCallback(
    (incoming: FileList | File[] | null | undefined) => {
      if (!incoming) return;
      const list = Array.from(incoming);
      if (!list.length) return;
      const allowed = validate(list);
      if (allowed.length) onFiles(allowed);
    },
    [validate, onFiles],
  );

  const dropProps = useMemo(
    () => ({
      onDragEnter: (e: React.DragEvent) => {
        if (disabled) return;
        // A dragged link, selection, or sortable row is not a file drop.
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        dragDepth.current += 1;
        setIsDragOver(true);
      },
      onDragOver: (e: React.DragEvent) => {
        if (disabled) return;
        if (!e.dataTransfer?.types?.includes('Files')) return;
        // Required on every dragover, not just the first, or the drop is refused.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      },
      onDragLeave: (e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setIsDragOver(false);
      },
      onDrop: (e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        resetDrag();
        acceptFiles(e.dataTransfer?.files);
      },
    }),
    [disabled, resetDrag, acceptFiles],
  );

  return { isDragOver, dropProps, acceptFiles };
}

export default useFileDrop;
